/**
 * Helpers for turning the denormalised `customers.tags` string into real tag objects.
 *
 * Background: a customer's tags live in two places. `customer_tags` is the relational
 * truth, but the customer payload only carries `customers.tags` - a comma separated
 * string of tag NAMES. The backend writes that string in two different formats:
 *
 *   update path (CustomerControllerHandler.kt:1029)  "A,B,"   trailing comma
 *   create path (CustomerControllerHandler.kt:568)   "A, B"   ", " separator, no trailing comma
 *
 * The previous implementation did `split(",")` then an unconditional `pop()`, which
 * silently discarded the last tag of any string in the create-path format, and matched
 * names with `===`, which produced `undefined` for any tag that had since been renamed.
 * Those `undefined` entries then threw on save and the error was swallowed, sending an
 * empty tag list and wiping the customer's tags.
 */

/**
 * Split a `customers.tags` string into trimmed, non-empty tag names.
 * Format agnostic: handles a trailing comma, a ", " separator, or neither.
 * @param {string} raw
 * @returns {string[]}
 */
export function parseTagNames (raw) {
  if (!raw || typeof raw !== 'string') return []
  return raw
    .split(',')
    .map(name => name.trim())
    .filter(name => name.length > 0)
}

/**
 * Canonical form for comparing two tag names.
 * Tag 11 is stored with a non-breaking space (U+00A0) while every saved customer string
 * uses a plain space, so a raw `===` never matches it.
 * @param {string} name
 * @returns {string}
 */
export function normaliseTagName (name) {
  if (!name || typeof name !== 'string') return ''
  return name
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
}

/**
 * Match tag names against the loaded tag options.
 *
 * Returns the resolved options and, separately, any names that could not be matched -
 * callers must never treat an unresolved name as "no tag", because that turns a display
 * problem into data loss.
 *
 * @param {string[]} names   tag names from parseTagNames()
 * @param {Array<{id:number, tag:string}>} options  tags loaded from /v1/tags/CUSTOMER
 * @returns {{selected: Array, unresolved: string[], optionsReady: boolean}}
 */
export function resolveTagNames (names, options) {
  const list = Array.isArray(names) ? names : []
  const opts = Array.isArray(options) ? options : []
  // An empty options list means the request has not come back yet. It is NOT the same as
  // "nothing matched" - the old `if (this.tags)` guard passed because [] is truthy.
  const optionsReady = opts.length > 0

  const selected = []
  const unresolved = []
  const seenIds = new Set()

  list.forEach(name => {
    const match = optionsReady
      ? opts.find(opt => normaliseTagName(opt.tag) === normaliseTagName(name))
      : undefined

    if (!match) {
      unresolved.push(name)
      return
    }
    if (seenIds.has(match.id)) return
    seenIds.add(match.id)
    selected.push(match)
  })

  return { selected, unresolved, optionsReady }
}

/**
 * Convert the selected tags into the `[{tagId}]` shape the backend expects.
 *
 * `invalid` is true when the selection contains anything that is not a real tag. The
 * caller must then omit `tags` from the request entirely rather than sending `[]`:
 * the backend only touches tags when the key is present (CustomerControllerHandler.kt:1026
 * and CustomerRepository.kt:461), so omitting it preserves what is already stored.
 *
 * An empty array is NOT invalid - deliberately clearing every tag is legitimate.
 *
 * @param {Array} tags
 * @returns {{payload: Array|null, invalid: boolean}}
 */
export function toTagIdPayload (tags) {
  if (!Array.isArray(tags)) return { payload: null, invalid: true }

  const payload = []
  const seenIds = new Set()

  for (const tag of tags) {
    if (!tag || tag.id === undefined || tag.id === null) {
      return { payload: null, invalid: true }
    }
    if (seenIds.has(tag.id)) continue
    seenIds.add(tag.id)
    payload.push({ tagId: tag.id })
  }

  return { payload, invalid: false }
}
